import axios from 'axios'
import { useEffect, useState } from 'react'
import { useInterval } from 'usehooks-ts'

import { usePageVisibility } from '@hooks/usePageVisibility'
import { actionReport } from '@methods/actionReport'
import { devLog } from '@methods/devLog'
import { forceRedirect } from '@methods/forceRedirect'

export interface UseScanQRParams {
  nextStep: () => void
  stayUrl?: string
  smsUrl?: string
  isSmsEnabled?: boolean
  retrySeconds?: number
  getProceedLinkUrl?: string
  isMobileSessionStartedUrl?: string
  closeDesktopSessionUrl?: string
}

export const useScanQR = ({
  nextStep,
  stayUrl,
  smsUrl,
  isSmsEnabled,
  retrySeconds,
  getProceedLinkUrl,
  isMobileSessionStartedUrl,
  closeDesktopSessionUrl,
}: UseScanQRParams) => {
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [isSendSmsButtonVisible, setIsSendSmsButtonVisible] = useState(true)
  const [sendSmsCount, setSendSmsCount] = useState(0)
  const [QRCodeUrl, setQRCodeUrl] = useState('' as string)

  const [counting, setCounting] = useState(false)
  const [countdownProgress, setCountdownProgress] = useState(retrySeconds ?? 0)
  const [shouldPoll, setShouldPoll] = useState(true)

  const isVisible = usePageVisibility()

  const getQRUrl = async () => {
    if (!getProceedLinkUrl) return

    try {
      const { data } = await axios.get(getProceedLinkUrl, {
        withCredentials: true,
      })

      setQRCodeUrl(data.proceedLink)
      devLog('QRCodeUrl', data.proceedLink)
    } catch (error) {
      devLog(error)
    }
  }

  const pollIsMobileSessionStarted = async () => {
    if (!isMobileSessionStartedUrl || !closeDesktopSessionUrl) return

    try {
      const { data } = await axios.get(isMobileSessionStartedUrl, {
        withCredentials: true,
      })

      if (data.isMobileSessionStarted) {
        setShouldPoll(false)

        if (data.disablePinCodeCheck) {
          forceRedirect(closeDesktopSessionUrl)
        } else {
          nextStep()
        }
      }
    } catch (error) {
      devLog(error)
    }
  }

  const handleTimeCount = () => {
    if (countdownProgress > 0) {
      setCountdownProgress(countdownProgress - 1)
    } else {
      setCountdownProgress(retrySeconds ?? 0)
      setCounting(false)
      setSendSmsCount(sendSmsCount + 1)
      setIsSendSmsButtonVisible(true)
      setDisabled(false)
    }
  }

  const sendSms = async () => {
    if (!isSmsEnabled || !smsUrl) return
    devLog('smsUrl', smsUrl)
    setIsSendSmsButtonVisible(false)
    setCounting(true)
    setDisabled(true)

    try {
      await axios.post(
        smsUrl,
        {},
        {
          withCredentials: true,
        }
      )
    } catch (error) {
      devLog(error)
    }
  }

  const stayOnDevice = () => {
    if (stayUrl?.length && !disabled) {
      setIsRedirecting(true)
      setDisabled(true)
      forceRedirect(stayUrl)
    }
  }

  useEffect(() => {
    actionReport({
      type: 'event.onboarding-web.go-to-mobile.DESKTOP_QR_CODE_PAGE_OPENED',
      payload: {},
    })

    getQRUrl()
  }, [])

  useEffect(() => {
    setShouldPoll(isVisible)
  }, [isVisible])

  useEffect(() => {
    shouldPoll && pollIsMobileSessionStarted()
  }, [shouldPoll])

  useInterval(pollIsMobileSessionStarted, shouldPoll ? 3000 : null)
  useInterval(handleTimeCount, counting ? 1000 : null)

  return {
    QRCodeUrl,
    isRedirecting,
    disabled,
    counting,
    isSendSmsButtonVisible,
    sendSmsCount,
    countdownProgress,
    sendSms,
    stayOnDevice,
  }
}
